import { useState, useEffect, useCallback } from 'react';
import { ShoppingCart, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { adminApi, AdminOrder } from '@/api/admin';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { btnOutlineNeon, btnPrimaryNeon, fieldClass } from '@/lib/admin-ui';
import { usePlatformPermission } from '@/hooks/use-platform-permission';
import { useUIStore } from '@/store/useUIStore';

const PAGE_LIMIT = 20;
const EMPTY_PAGINATION = {
  page: 1,
  limit: PAGE_LIMIT,
  total: 0,
  totalPages: 0,
};

export function OrderManagementPage() {
  const isLight = useUIStore((s) => s.theme === 'light');
  const lightFieldClass =
    'border-slate-300 bg-white text-slate-800 placeholder:text-slate-400 focus-visible:ring-cyan-500/35';
  const canViewOrders = usePlatformPermission('platform:order:view');
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(EMPTY_PAGINATION);
  const [searchTerm, setSearchTerm] = useState('');

  const loadOrders = useCallback(async () => {
    if (!canViewOrders) {
      setOrders([]);
      setPagination(EMPTY_PAGINATION);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await adminApi.getAllOrders(pagination.page, PAGE_LIMIT);
      setOrders(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error('加载订单失败:', error);
    } finally {
      setLoading(false);
    }
  }, [canViewOrders, pagination.page]);

  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  function getStatusVariant(status: AdminOrder['status']): 'default' | 'secondary' | 'destructive' | 'outline' {
    const variants: Record<AdminOrder['status'], 'default' | 'secondary' | 'destructive' | 'outline'> = {
      PENDING: 'secondary',
      PAID: 'default',
      FAILED: 'destructive',
      REFUNDED: 'outline',
    };
    return variants[status] ?? 'outline';
  }

  function getTypeLabel(type: AdminOrder['type']) {
    const labels = {
      NEW: '新购',
      RENEW: '续费',
      UPGRADE: '升级',
      DOWNGRADE: '降级',
    };
    return labels[type] || type;
  }

  return (
    <div className="space-y-6">
      {!canViewOrders && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
          当前角色无订单列表权限（
          <code className="font-mono text-amber-200/90">platform:order:view</code>
          ），下方列表为空。请联系管理员调整平台角色权限。
        </div>
      )}
      {/* 页面标题 */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">订单管理</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          管理系统所有订单和交易记录
        </p>
      </div>

      {/* 搜索栏 */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder="搜索订单号、租户名称..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={cn('pl-10', fieldClass, isLight && lightFieldClass)}
              />
            </div>
            <Button type="button" className={btnPrimaryNeon}>
              搜索
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 订单表格 */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">订单列表</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>订单号</TableHead>
                <TableHead>租户</TableHead>
                <TableHead>系统/版本</TableHead>
                <TableHead>类型</TableHead>
                <TableHead>金额</TableHead>
                <TableHead>状态</TableHead>
                <TableHead>创建时间</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-24 text-center">
                    加载中...
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                    暂无订单数据
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell className="font-medium">#{order.id.slice(0, 8)}</TableCell>
                    <TableCell>{order.tenantName}</TableCell>
                    <TableCell>
                      {order.systemName} · {order.planName}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{getTypeLabel(order.type)}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">
                      ¥{(order.amount / 100).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusVariant(order.status)}>
                        {order.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(order.createdAt).toLocaleDateString('zh-CN')}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>

        {/* 分页 */}
        {pagination.totalPages > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t">
            <div className="text-sm text-muted-foreground">
              共 {pagination.total} 条记录，第 {pagination.page} / {pagination.totalPages} 页
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className={btnOutlineNeon}
                onClick={() =>
                  setPagination((prev) => ({ ...prev, page: prev.page - 1 }))
                }
                disabled={pagination.page === 1}
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className={btnOutlineNeon}
                onClick={() =>
                  setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
                }
                disabled={pagination.page >= pagination.totalPages}
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
